import { Card, Text } from '@chakra-ui/react'
import type {
  AppInfoMessage,
  AppInfoMessageFilter,
  AppInfoMessagePagedQuery,
} from 'BrokerTsLib/src/dtos'
import {
  useGetAllAppInfoMessages,
  useSetReadAppInfoMessage,
} from 'BrokerTsLib/src/hooks/useAppInfoMessage'
import { useCallback, useEffect, useState } from 'react'

import { News } from '@/components/News'
import { NewsFilter } from '@/components/News/NewsFilter'
import { DashboardHeader } from '@/containers/dashboard/DashboardHeader'
import { useApp } from '@/hooks/useApp'
import { PageWrapper } from '@/layouts/components/PageWrapper'

const pageSize = 5

export const NewsPage = () => {
  const { product } = useApp()
  
  const {
    mutateAsync: getMessages,
    data: dataMessages,
    isLoading: isLoading,
  } = useGetAllAppInfoMessages()
  const [filter, setFilter] = useState<AppInfoMessagePagedQuery>({
    product: product ?? undefined,
    pageIndex: 1,
    pageSize,
    shown: true,
    unread: false,
  })

  useEffect(() => {
    if (product !== filter.product) {
      setFilter((value) => ({
        ...value,
        product: product ?? undefined,
        pageIndex: 1,
      }))
    }
  }, [product])

  const { mutateAsync: setReadAsync } = useSetReadAppInfoMessage(
    product ?? undefined,
  )

  const setRead = (id: number) => {
    setReadAsync({ read: true, id }).then(() => {
      setNews((value) =>
        value.map((item) => (item.id === id ? { ...item, read: true } : item)),
      )
    })
  }

  const [news, setNews] = useState<AppInfoMessage[]>([])

  const filteredNews = news.filter((item) => item.read !== filter.unread)

  useEffect(() => {
    getMessages(filter)
  }, [filter, getMessages])

  useEffect(() => {
    const newItems = dataMessages?.data.items ?? []
    setNews((value) => [...value, ...newItems])
  }, [dataMessages?.data.items])

  const updatePagedQuery = (updatedFilter: AppInfoMessageFilter) => {
    if (!(updatedFilter.to instanceof Date)) updatedFilter.to = undefined

    if (!(updatedFilter.from instanceof Date)) updatedFilter.from = undefined

    if (
      updatedFilter &&
      (filter.unread !== updatedFilter.unread ||
        filter.from?.getTime() !== updatedFilter.from?.getTime() ||
        filter.to?.getTime() !== updatedFilter.to?.getTime())
    ) {
      setNews([])
      setFilter((value) => ({ ...value, ...updatedFilter, pageIndex: 1 }))
    }
  }

  const loadMore = useCallback(() => {
    setFilter((value) => ({
      ...value,
      pageIndex: (value.pageIndex ?? 1) + 1,
    }))
  }, [setFilter])

  return (
    <PageWrapper
      header={<DashboardHeader title="Aktuality" />}
      mainProps={{
        sx: {
          width: '100%',
          maxW: { base: 'full', lg: '4xl' },
          margin: '0 auto',
          py: {base: 0, md: 8},
        },
      }}
    >
      <Card py={{base: 4, md: 8}} px={{base: 4, md: 12}}>
        <NewsFilter filter={filter} onSubmit={updatePagedQuery} />
        <News
          news={filteredNews!}
          isLoading={isLoading}
          onSetRead={setRead}
          onMoreClick={
            (dataMessages?.data.totalCount ?? 0) > news.length
              ? loadMore
              : undefined
          }
        >
          {filteredNews.length === 0 && !isLoading && (
            <Text textAlign="center">Žádné aktuality</Text>
          )}
        </News>
      </Card>
    </PageWrapper>
  )
}
